import { fade } from "material-ui/utils/colorManipulator";
import getMuiTheme from "material-ui/styles/getMuiTheme";
import * as Colors from "material-ui/styles/colors";

const rawTheme = {
    spacing: {
        iconSize: 24,
        desktopGutter: 16,
        desktopGutterMore: 24,
        desktopGutterLess: 16,
        desktopGutterMini: 8,
        desktopKeylineIncrement: 64,
        desktopDropDownMenuItemHeight: 32,
        desktopDropDownMenuFontSize: 15,
        desktopLeftNavMenuItemHeight: 48,
        desktopSubheaderHeight: 48,
        desktopToolbarHeight: 56,
    },
    breakpoints: {
        xs: 480,
        sm: 768,
        md: 992,
        lg: 1200,
    },
    fontFamily: "Lato, sans-serif",
    colors: Colors,
    palette: {
        primary1Color: "#2fc7be",
        primary2Color: Colors.grey600,
        primary3Color: Colors.grey300,
        accent1Color: "#2fc7be",
        accent2Color: "#568ede",
        accent3Color: Colors.grey600,
        accent4Color: "#1fc8bf",
        textColor: "#2d2d2d",
        secondaryTextColor: "#8e8e8e",
        interactiveTextColor: "#007571",
        alternateTextColor: Colors.white,
        canvasColor: Colors.white,
        borderColor: "#e1e1e1",
        disabledColor: fade(Colors.grey900, 0.3),
        primaryHover: fade("#2fc7be", 0.3),
        darkestGrey: "#1e1e1e",
        darkerGrey: "#2d2d2d",
        darkGrey: "#585858",
        grey: "#bdbdbd",
        lightGrey: "#e1e1e1",
        lighterGrey: "#f0f0f0",
        lightestGrey: "#f5f5f5",
        disabledButtonColor: "#F5F5F5",
        blue: "#639cd2",
        blueHover: fade("#639cd2", 0.7),
        lightBlue: "#609BD4",
        lighterBlue: "#EBF6FD",
        mint: "#00A9A4",
        darkMint: "#00A39E",
        orange: "#f79256",
        shadow: "rgba(45,45,45,0.3)",
        error: "#e33729",
        errorLighter: "#fff1f0",
        errorAlertBackground: "#fbf3f2",
        errorAlertBorderColor: "#f0cdc9",
        errorAlertTextColor: "#d4948d",
        errorLightBackground: "#fff5f4",
        errorLightBorder: "#FFBBB6",
        warningBase: "#ffd300",
        warningAlertBackground: "#fffdd9",
        warningAlertBorderColor: "#f8f48b",
        warningAlertTextColor: "#8a8538",
        warningAlertIconSignColor: "#e7b231",
        infoAlertBackground: "#EAF5FF",
        infoAlertBorderColor: "#B0D4F7",
        infoAlertTextColor: "#639CD2",
        successAlertBackground: "#F1FBF4",
        successAlertBorderColor: "#9FD5B4",
        successAlertTextColor: "#4AA06B",
        warning: "#e7645c", // aka salmon color
        warningAlertIconColor: Colors.yellowA400,
        editing: "#fffcda",
        errorBackgroundColor: "#FFCBC7",
        errorColor: "#E7645C",
        errorColor2: "#CD4942",
        positive: "#478055",
        placeholderText: "#bdbdbd",
        timelineBackgroundColor: "#F5F5F5",
        unreadBackground: "#ECF5F9",
        selectedOptionBackground: "#B7E4FF",
        rollupHeaderBackgound: "#2a3e54",
        toolbarBorderColor: "#94a6bd",
        toolbarIconColor: "#8f8f8f",
        darkPrimary1: "#48a6a2",
        greyTag: "#9f9f9f",
        tileItemText: "#989898",
        swatchBorderColor: "#cccccc",
        swatchBackgroundColor: "#f7f7f7",
        white: "#ffffff",
        black: "#000",
        offBlack: "#2b2b2b",
        tileItemShadowColor: "#cdcdcd",
        borderGrey: "#cfd1d1",
    },
    preloadSectionHeight: {
        catalog: 430,
        items: 400,
    },
};

interface ITheme extends __MaterialUI.Styles.MuiTheme {
    pageHeader: {
        height: number;
    };
    type: any;
    nav: any;
    shadow: string;
    filterBar: any;
    searchBar: any;
    sidebar: {
        width: number;
    };
    insightsSidePanel: {
        width: number;
    };
    static: boolean;
    editIcon: any;
    PDPThumbnail: {
        width: number;
        height: number;
    };
    palette: {
        primary1Color: string;
        primary2Color: string;
        primary3Color: string;
        accent1Color: string;
        accent2Color: string;
        accent3Color: string;
        accent4Color: string;
        textColor: string;
        secondaryTextColor: string;
        interactiveTextColor: string;
        alternateTextColor: string;
        canvasColor: string;
        borderColor: string;
        disabledColor: string;
        primaryHover: string;
        darkestGrey: string;
        darkerGrey: string;
        darkGrey: string;
        grey: string;
        lightGrey: string;
        lighterGrey: string;
        lightestGrey: string;
        blue: string;
        blueHover: string;
        lightBlue: string;
        lighterBlue: string;
        mint: string;
        darkMint: string;
        toolbarBorderColor: string;
        toolbarIconColor: string;
        orange: string;
        shadow: string;
        error: string;
        errorLighter: string;
        errorAlertBackground: string;
        errorAlertBorderColor: string;
        errorAlertTextColor: string;
        errorLightBackground: string;
        errorLightBorder: string;
        warningBase: string;
        warningAlertBackground: string;
        warningAlertBorderColor: string;
        warningAlertTextColor: string;
        warningAlertIconSignColor: string;
        infoAlertBackground: string;
        infoAlertBorderColor: string;
        infoAlertTextColor: string;
        successAlertBackground: string;
        successAlertBorderColor: string;
        successAlertTextColor: string;
        warning: string;
        warningAlertIconColor: string;
        editing: string;
        errorBackgroundColor: string;
        placeholderText: string;
        disabledButtonColor: string;
        timelineBackgroundColor: string;
        unreadBackground: string;
        selectedOptionBackground: string;
        rollupHeaderBackgound: string;
        errorColor: string;
        errorColor2: string;
        positive: string;
        darkPrimary1: string;
        greyTag: string;
        tileItemText: string;
        swatchBorderColor: string;
        swatchBackgroundColor: string;
        white: string;
        black: string;
        offBlack: string;
        tileItemShadowColor: string;
        borderGrey: string;
    };
    grid: {
        totalAllocationDimensionColumn: string;
        headerBackgroundColor: string;
        notEditableBackgroundColor: string;
        sectionSeparatorColor: string;
    };
    agGrid: {
        defaultTheme: string;
    };
}

const theme = getMuiTheme(rawTheme) as ITheme;

theme.button.height = 40;
theme.button.textTransform = "none";

theme.toolbar.backgroundColor = "white";

theme.type = {
    headline: {
        fontSize: "32px",
        fontWeight: 400,
        margin: "0px",
        padding: "0px",
        color: rawTheme.palette.darkerGrey,
    },
    title: {
        fontSize: "24px",
        fontWeight: 400,
        margin: "0px",
        padding: "0px",
        color: rawTheme.palette.darkerGrey,
    },
    subheader: {
        fontSize: "20px",
        fontWeight: 400,
        margin: "0px",
        padding: "0px",
        color: rawTheme.palette.darkerGrey,
    },
    body2: {
        fontSize: "14px",
        fontWeight: 400,
        margin: "0px",
        padding: "0px",
        color: "#585858",
    },
    label: {
        fontSize: "12px",
        fontWeight: 400,
        color: "#585858",
    },
};

theme.checkbox.boxColor = rawTheme.palette.grey;
theme.checkbox.checkedColor = rawTheme.palette.accent1Color;
theme.checkbox.disabledColor = rawTheme.palette.lighterGrey;

theme.toggle.thumbDisabledColor = "#f0f0f0";
theme.toggle.thumbOffColor = "white";
theme.toggle.thumbOnColor = "white";
theme.toggle.thumbRequiredColor = rawTheme.palette.accent1Color;
theme.toggle.trackDisabledColor = "#B1AFB0";
theme.toggle.trackOffColor = "#B1AFB0";
theme.toggle.trackOnColor = "#75C2BC";
theme.toggle.trackRequiredColor = "#75C2BC";

theme.subheader.color = rawTheme.palette.darkerGrey;
theme.subheader.fontWeight = 600;

theme.shadow = "0px 4px 12px " + rawTheme.palette.shadow;

theme.filterBar = {
    width: 240,
};

theme.searchBar = {
    height: 52,
};

theme.sidebar = {
    width: 384,
};

theme.insightsSidePanel = {
    width: 384,
};

theme.appBar.height = 60;

theme.pageHeader = {
    height: 60,
};

theme.static = true;

theme.editIcon = {
    height: "14px",
    color: rawTheme.palette.grey,
};

theme.nav = {
    iconWidth: 60,
    iconAndTextWidth: 240,
};

theme.zIndex.layer = 2600;
theme.zIndex.popover = 3000;

theme.PDPThumbnail = {
    width: 40,
    height: 68,
};

theme.grid = {
    totalAllocationDimensionColumn: "#f5f7f7",
    headerBackgroundColor: "#f5f7f7",
    notEditableBackgroundColor: "#e5e7e7",
    sectionSeparatorColor: "#7d8e87",
};

theme.agGrid = {
    defaultTheme: "ag-theme-balham",
};

export { rawTheme, theme };
export default theme;
